import type { PlanStatus } from '@steado/admin-contract/users';

import type { ToneName } from '@/components/table/tone';

/**
 * The badge tone for a plan week's status, in one place.
 *
 * The list and the detail screen both draw this badge, and an ACTIVE week that
 * is green in a table and grey in a header reads as two different things.
 *
 * There is no fallback branch: `PlanStatus` is a closed `z.enum`, so a status
 * this build has never heard of is rejected when the response is parsed rather
 * than arriving here to be coloured grey. `Record<PlanStatus, …>` then makes a
 * new status a compile error instead of a silently mute badge.
 */
const PLAN_TONES: Readonly<Record<PlanStatus, ToneName>> = {
  active: 'ok',
  generating: 'info',
  failed: 'err',
  superseded: 'mute',
  // In-flight, like `generating`: the user still has their previous plan and a
  // replacement is being built.
  reconciling: 'info',
  // Not an error, but the only status where a user is being shown less than
  // their plan says, so it should stand out rather than read as routine.
  safety_hold: 'warn',
  // Never became anyone's plan. Grey with `superseded`, which it is the
  // never-activated twin of.
  obsolete: 'mute',
};

export function planTone(status: PlanStatus): ToneName {
  return PLAN_TONES[status];
}
